import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import newApi from "@services/newApi/api";
import type { IEnvelope } from "@services/newApi/typed";
import { queryKeys } from "@lib/queryKeys";
import type {
  IApproveUserResponse,
  ICreateUserParams,
  ICreateUserResponse,
  IEditUserParams,
  IEditUserResponse,
  IInactiveUserParams,
  IInactiveUserResponse,
  IUpdateUserSpecialtyParams,
  IUpdateUserSpecialtyResponse,
  IUser,
} from "../types";

/**
 * Mutations react-query para o domínio Users.
 *
 * Substituem os métodos imperativos de `contexts/Users/utils.ts`.
 * Cada mutation invalida `queryKeys.users.all()` em `onSuccess` para refletir
 * mudanças nas listas. Consumidores que precisam controle fino (optimistic
 * updates, snapshot/rollback) seguem usando o `useUsersQuery` agregador.
 */

interface IApproveUserArgs {
  id: number;
  data: Partial<IUser>;
}

export const useApproveUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: IApproveUserArgs) => {
      // Médico (role 4) → rota de aprovação de médico (exige hash/cod_doctor,
      // enviados por ApproveDoctor). Demais → rota de ativação (status → 2 +
      // e-mail de cadastro de senha). Sem isto, aprovar não-médico batia em
      // /doctor-users e retornava 400 (hash/cod_doctor Required).
      const isDoctor = (data as { role_id?: number })?.role_id === 4;
      const response = isDoctor
        ? await newApi.post<IEnvelope<IApproveUserResponse>>(
            `/users/doctor-users/${id}/approval`,
            data,
          )
        : await newApi.post<IEnvelope<IApproveUserResponse>>(
            `/users/hospital-users/${id}/approval`,
            { active: true },
          );
      toast.success("Usuário aprovado com sucesso.");
      return response.data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      console.error("Erro na aprovação do usuário:", error?.message);
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ICreateUserParams) => {
      const response = await newApi.post<ICreateUserResponse>(
        `/users/hospital-users`,
        data,
      );
      toast.success("Usuário criado com sucesso.");
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

/**
 * Criação de usuário de convênio pela GESTÃO (rota autenticada).
 *
 * A tela de Novo Usuário usava a rota PÚBLICA de auto-cadastro
 * (`user/convenio/register`, `newApi_public`): o `cd_convenio` saía de um select
 * com 156 convênios e a identidade do ator nem trafegava — papel 6 criava
 * usuário em convênio alheio (G12). Aqui o backend força o escopo do ator.
 */
export const useCreateConvenioUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await newApi.post<ICreateUserResponse>(
        `/users/convenio`,
        data,
      );
      toast.success("Usuário criado com sucesso.");
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

interface IEditUserArgs {
  userId: number;
  data: IEditUserParams;
}

export const useEditUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: IEditUserArgs) => {
      const response = await newApi.put<IEditUserResponse>(
        `/users/${userId}`,
        data,
      );
      toast.success("Usuário atualizado com sucesso.");
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

interface IEditDoctorArgs {
  userId: number | string;
  data: any;
}

export const useEditDoctorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: IEditDoctorArgs) => {
      const response = await newApi.put(`/users/${userId}`, data);
      toast.success("Dados do médico atualizados com sucesso.");
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

export const useEditUserImageMutation = () =>
  useMutation({
    mutationFn: async (data: any) => {
      const response = await newApi.post(`/users/image`, data);
      return response;
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });

interface IInactivateUserArgs {
  userId: number;
  data: IInactiveUserParams;
}

export const useInactivateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: IInactivateUserArgs) => {
      const response = await newApi.put<IInactiveUserResponse>(
        `/users/${userId}/activation`,
        data,
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      console.error("Erro na ativação/inativação do usuário:", error?.message);
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

interface ISetUserPermissionsArgs {
  userId: number;
  scoped: Array<{
    // Alinhado ao backend (spec 006): HOSPITAL/CONVENIO exigem scopeId; SELF/GLOBAL
    // são sem id (scopeId=null). Envie só os escopos alterados (omitido = no-op).
    scopeType: "HOSPITAL" | "CONVENIO" | "SELF" | "GLOBAL";
    scopeId: number | null;
    permissions: number[];
  }>;
  /** Spec 20261028-132000 AC-4: the Operação access on or off; omitted leaves it as it is. */
  operacao?: boolean;
}

/**
 * Motor de permissões: define os grants do usuário por escopo (hospital/convênio),
 * via PUT /users/:id/permissions. Não-destrutivo no backend (deny em vez de apagar).
 */
export const useSetUserPermissionsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, scoped, operacao }: ISetUserPermissionsArgs) => {
      const response = await newApi.put(`/users/${userId}/permissions`, {
        scoped,
        ...(operacao !== undefined ? { operacao } : {}),
      });
      return response;
    },
    onSuccess: async () => {
      // `users.all()` already reaches the user's detail; the `['user', id]` invalidation reached nothing (P20EE-184).
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};

interface IUpdateUserSpecialtyArgs {
  userId: number;
  data: IUpdateUserSpecialtyParams;
}

export const useUpdateUserSpecialtyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: IUpdateUserSpecialtyArgs) => {
      const response = await newApi.put<IUpdateUserSpecialtyResponse>(
        `users/${userId}`,
        data,
      );
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      if (message) toast.error(message);
    },
  });
};
